/* Photograph-preserving Musht drink motion. No product, cart or text behavior. */
const script = [...document.scripts].find(node => node.dataset.fluidModule && /mf-drink-motion\.js/.test(node.src));
const fluidURL = script?.dataset.fluidModule || new URL('./mf-drink-fluid.js', import.meta.url).href;
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const coarse = matchMedia('(pointer: coarse)');
const records = new Map();
const sources = new Map();
const PROFILES = Object.freeze({
  'mf-iced.webp': { name: 'iced', width: 1100, height: 1375, cx: 573, cy: 758, rx: 170, ry: 160, feather: .075, force: .30 },
  'mf-warm.webp': { name: 'hot', width: 1500, height: 1000, cx: 916, cy: 304, rx: 224, ry: 112, feather: .080, force: .26 },
  'mf-coffee-pour.png': { name: 'coffee', width: 1254, height: 1254, cx: 655, cy: 535, rx: 239, ry: 118, feather: .076, force: .28, freeze: { cx: 701, cy: 456, rx: 36, ry: 90 } }
});
let modulePromise, running = 0, syncQueued = false, nextId = 1, paused = false, suspended = false;
try { paused = sessionStorage.getItem('mf-drink-motion-paused') === 'true'; } catch { /* Storage is optional. */ }
const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
const sourceURL = image => image.currentSrc || image.src;
function profileFor(image) {
  const url = image.src || sourceURL(image);
  return Object.entries(PROFILES).find(([name]) => url.split(/[?#]/)[0].endsWith('/' + name))?.[1] || null;
}
function status(record, value) {
  record.state = value;
  record.host.dataset.mfMotionState = value;
  record.image.dataset.mfMotionState = value;
}
function loadSource(url) {
  if (sources.has(url)) return sources.get(url);
  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Drink photograph cannot be sampled.'));
    image.src = url;
  });
  sources.set(url, promise);
  if (sources.size > 4) sources.delete(sources.keys().next().value);
  return promise;
}
function samplePhoto(image, profile, size) {
  const surface = document.createElement('canvas');
  surface.width = surface.height = size;
  const context = surface.getContext('2d');
  if (!context) throw new Error('Photograph sampling unavailable.');
  const sx = image.naturalWidth / profile.width, sy = image.naturalHeight / profile.height;
  context.drawImage(image, (profile.cx - profile.rx) * sx, (profile.cy - profile.ry) * sy,
    profile.rx * 2 * sx, profile.ry * 2 * sy, 0, 0, size, size);
  return surface;
}
// object-position percentages apply to the unused space, including negative
// space from a cover crop. This keeps the liquid aligned at every breakpoint.
function positionOffset(token, space) {
  if (!token || token === 'center') return space * .5;
  if (token === 'left' || token === 'top') return 0;
  if (token === 'right' || token === 'bottom') return space;
  if (token.endsWith('%')) return space * parseFloat(token) / 100;
  if (token.startsWith('calc(')) {
    const percent = token.match(/(-?[\d.]+)%/);
    const pixels = token.match(/([+-])\s*([\d.]+)px/);
    return space * (percent ? +percent[1] / 100 : 0) + (pixels ? (pixels[1] === '-' ? -1 : 1) * +pixels[2] : 0);
  }
  return Number.parseFloat(token) || 0;
}
function mapPhoto(record) {
  const { image, host, profile, layer, canvas, button } = record;
  if (!profile) return;
  const css = getComputedStyle(image), bounds = image.getBoundingClientRect(), frame = host.getBoundingClientRect();
  const hostScaleX = frame.width / (host.offsetWidth || frame.width || 1);
  const hostScaleY = frame.height / (host.offsetHeight || frame.height || 1);
  const paddingX = parseFloat(css.paddingLeft) + parseFloat(css.paddingRight);
  const paddingY = parseFloat(css.paddingTop) + parseFloat(css.paddingBottom);
  const width = image.clientWidth - paddingX, height = image.clientHeight - paddingY;
  const left = (bounds.left - frame.left) / hostScaleX - host.clientLeft + host.scrollLeft + parseFloat(css.borderLeftWidth) + parseFloat(css.paddingLeft);
  const top = (bounds.top - frame.top) / hostScaleY - host.clientTop + host.scrollTop + parseFloat(css.borderTopWidth) + parseFloat(css.paddingTop);
  if (width < 1 || height < 1) return;
  const iw = image.naturalWidth || profile.width, ih = image.naturalHeight || profile.height;
  let scaleX = width / iw, scaleY = height / ih;
  if (css.objectFit !== 'fill') {
    let scale = css.objectFit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
    if (css.objectFit === 'none') scale = 1;
    if (css.objectFit === 'scale-down') scale = Math.min(1, scale);
    scaleX = scaleY = scale;
  }
  const positions = css.objectPosition.match(/calc\([^)]+\)|[^\s]+/g) || ['50%', '50%'];
  const ox = positionOffset(positions[0], width - iw * scaleX), oy = positionOffset(positions[1] || '50%', height - ih * scaleY);
  const xScale = scaleX * iw / profile.width, yScale = scaleY * ih / profile.height;
  const rect = { x: ox + (profile.cx - profile.rx) * xScale, y: oy + (profile.cy - profile.ry) * yScale,
    width: profile.rx * 2 * xScale, height: profile.ry * 2 * yScale };
  record.mapping = { ...rect, boxWidth: width, boxHeight: height };
  if (layer) {
    Object.assign(layer.style, { left: left + 'px', top: top + 'px', width: width + 'px', height: height + 'px', borderRadius: css.borderRadius });
    Object.assign(canvas.style, { left: rect.x + 'px', top: rect.y + 'px', width: rect.width + 'px', height: rect.height + 'px', filter: css.filter, opacity: css.opacity, mixBlendMode: css.mixBlendMode });
  }
  if (button) {
    const gallery = image.hasAttribute('data-mf-gallery-image');
    Object.assign(button.style, { left: (gallery ? left + Math.max(12, width - 132) : left + 12) + 'px', top: (gallery ? top + Math.max(12, height - 56) : top + 12) + 'px' });
  }
}
function release(record) {
  record.generation++;
  record.initializing = false;
  const fluid = record.fluid;
  record.fluid = null;
  record.canvas = null;
  record.layer?.remove(); record.layer = null;
  record.last = 0; record.previous = record.tap = null;
  try { fluid?.dispose(); } catch { /* The original photograph remains visible. */ }
}
function fail(record, error) {
  release(record); record.failed = true;
  record.error = String(error?.message || error || 'Animation unavailable');
  if (record.button) record.button.hidden = true;
  status(record, 'still');
}
function updateControl(record) {
  if (!record.button) {
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'mf-motion-toggle';
    button.addEventListener('click', () => {
      paused = !paused;
      try { sessionStorage.setItem('mf-drink-motion-paused', String(paused)); } catch { /* Page preference still works. */ }
      queueSync();
    });
    record.host.append(button); record.button = button;
  }
  const label = paused ? 'Resume motion' : 'Pause motion';
  if (record.button.textContent !== label) record.button.textContent = label;
  record.button.setAttribute('aria-pressed', String(paused));
  record.button.title = paused ? 'Resume drink motion on this page' : 'Pause drink motion on this page';
  record.button.hidden = reduced.matches || record.failed || !record.profile;
  mapPhoto(record);
}
async function initialize(record) {
  if (record.fluid || record.initializing || record.failed) return;
  const generation = ++record.generation, url = sourceURL(record.image), profile = record.profile;
  record.initializing = true; status(record, 'loading');
  try {
    modulePromise ||= import(fluidURL);
    const [{ DrinkFluid }, image] = await Promise.all([modulePromise, loadSource(url)]);
    if (generation !== record.generation || !record.active || paused || suspended || reduced.matches || document.hidden || !record.image.isConnected) return;
    const canvas = document.createElement('canvas'), layer = document.createElement('div');
    canvas.className = 'mf-drink-surface'; canvas.setAttribute('aria-hidden', 'true');
    canvas.dataset.running = 'false'; canvas.dataset.frames = '0'; canvas.dataset.splats = '0'; canvas.dataset.profile = profile.name;
    layer.className = 'mf-drink-crop'; layer.setAttribute('aria-hidden', 'true'); layer.append(canvas);
    const mobile = coarse.matches || innerWidth < 761;
    const f = profile.freeze;
    const freeze = f ? [(f.cx - profile.cx + profile.rx) / (2 * profile.rx), (f.cy - profile.cy + profile.ry) / (2 * profile.ry), f.rx / (2 * profile.rx), f.ry / (2 * profile.ry)] : null;
    const fluid = new DrinkFluid(canvas, samplePhoto(image, profile, mobile ? 256 : 512), {
      simulationResolution: mobile ? 96 : 160, colorResolution: mobile ? 256 : 384,
      outputResolution: mobile ? 256 : 512, pressureIterations: mobile ? 16 : 18,
      idleForce: profile.force, maxVelocity: .10, pointerForce: .07, tapSwirl: .12,
      edgeFeather: profile.feather, pigmentAmount: 0, freeze
    });
    record.fluid = fluid; record.canvas = canvas; record.layer = layer; record.url = url;
    record.elapsed = 0; record.idle = 10; record.last = 0;
    canvas.addEventListener('webglcontextlost', event => {
      if (record.canvas !== canvas || !record.fluid) return;
      event.preventDefault(); fail(record, new Error('WebGL context lost'));
    });
    record.host.append(layer); mapPhoto(record); fluid.render();
    record.initializing = false; status(record, 'running'); updateControl(record); startLoop();
  } catch (error) { if (generation === record.generation) fail(record, error); }
}
function sync() {
  syncQueued = false;
  const eligible = [];
  for (const record of records.values()) {
    if (!record.image.isConnected) { unregister(record); continue; }
    record.profile = profileFor(record.image);
    const visible = record.visible && record.image.getBoundingClientRect().width >= 180;
    if (!record.profile || !visible || !record.image.complete || !record.image.naturalWidth || record.failed || reduced.matches || document.hidden || suspended || paused) {
      record.active = false;
      if (record.fluid || record.initializing) release(record);
      status(record, !record.profile ? 'static-photo' : record.failed ? 'still' : reduced.matches ? 'reduced-motion' : paused ? 'paused' : document.hidden || suspended ? 'hidden' : 'offscreen');
      if (record.button) record.button.hidden = !record.profile || reduced.matches || record.failed;
      if (paused && visible && record.profile && !reduced.matches && !record.failed) updateControl(record);
      continue;
    }
    eligible.push(record);
  }
  const limit = coarse.matches || innerWidth < 761 ? 1 : 2;
  eligible.sort((a, b) => b.ratio - a.ratio || a.id - b.id);
  eligible.forEach((record, index) => {
    record.active = index < limit;
    if (!record.active) { if (record.fluid || record.initializing) release(record); if (record.button) record.button.hidden = true; status(record, 'standby'); return; }
    updateControl(record); initialize(record);
    if (record.fluid) { mapPhoto(record); status(record, 'running'); }
  });
  if ([...records.values()].some(record => record.fluid && record.active)) startLoop();
}
function queueSync() { if (!syncQueued) { syncQueued = true; queueMicrotask(sync); } }
function startLoop() { if (!running) running = requestAnimationFrame(tick); }
function tick(now) {
  running = 0;
  if (document.hidden || suspended || paused || reduced.matches) { queueSync(); return; }
  let active = false;
  const interval = 1000 / (coarse.matches || innerWidth < 761 ? 24 : 30);
  for (const record of records.values()) {
    if (!record.active || !record.fluid) continue;
    active = true;
    if (record.last && now - record.last < interval - 1) continue;
    const dt = record.last ? Math.min((now - record.last) / 1000, .05) : 1 / 30;
    record.last = now; record.elapsed += dt; record.idle += dt;
    try {
      record.fluid.step(dt, { elapsed: record.elapsed, idleSeconds: record.idle }); record.fluid.render();
      const stats = record.fluid.getStats();
      record.canvas.dataset.running = 'true'; record.canvas.dataset.frames = String(stats.frames); record.canvas.dataset.splats = String(stats.splats);
    } catch (error) { fail(record, error); }
  }
  if (active) startLoop();
}
function point(record, event) {
  if (!record.fluid || !record.canvas || !record.active || paused || reduced.matches || event.target.closest('.mf-motion-toggle')) return null;
  const bounds = record.canvas.getBoundingClientRect();
  const p = { x: (event.clientX - bounds.left) / bounds.width, y: (event.clientY - bounds.top) / bounds.height };
  const photo = record.image.getBoundingClientRect();
  if (event.clientX < photo.left || event.clientX > photo.right || event.clientY < photo.top || event.clientY > photo.bottom) return null;
  if (Math.hypot(p.x - .5, p.y - .5) > .46) return null;
  const f = record.fluid.options.freeze;
  if (f && Math.hypot((p.x - f[0]) / f[2], (p.y - f[1]) / f[3]) < 1.28) return null;
  return p;
}
function stir(record, p, dx, dy, tap) {
  if (!p || !record.fluid) return;
  record.fluid.splat({ ...p, dx, dy, tap, amount: 1 }); record.idle = 0;
}
function register(image) {
  if (records.has(image) || image.closest('button,a')) return;
  if (!profileFor(image) && !image.hasAttribute('data-mf-gallery-image') && !image.hasAttribute('data-method-image')) return;
  const host = image.parentElement;
  const record = { id: nextId++, image, host, profile: profileFor(image), generation: 0, state: 'waiting', visible: false, ratio: 0, active: false, failed: false };
  records.set(image, record);
  host.classList.add('mf-drink-motion-host');
  if (getComputedStyle(host).position === 'static') { host.classList.add('mf-drink-motion-positioned'); record.positioned = true; }
  const abort = new AbortController(); record.abort = abort;
  const listen = (node, name, callback) => node.addEventListener(name, callback, { passive: true, signal: abort.signal });
  listen(host, 'pointermove', event => {
    if (event.pointerType === 'touch') return;
    const p = point(record, event);
    if (!p) { record.previous = null; return; }
    if (record.previous) {
      const dt = Math.max((event.timeStamp - record.previous.time) / 1000, 1 / 120);
      const dx = clamp((p.x - record.previous.x) / dt, -.5, .5), dy = clamp((p.y - record.previous.y) / dt, -.5, .5);
      if (Math.hypot(dx, dy) > .003) stir(record, p, dx, dy, false);
    }
    record.previous = { ...p, time: event.timeStamp };
  });
  listen(host, 'pointerleave', () => { record.previous = null; });
  listen(host, 'pointerdown', event => {
    const p = point(record, event); if (!p) return;
    if (event.pointerType === 'touch') record.tap = { ...p, sx: event.clientX, sy: event.clientY, time: event.timeStamp, id: event.pointerId };
    else stir(record, p, .025, -.02, true);
  });
  listen(host, 'pointerup', event => {
    const tap = record.tap;
    if (tap && tap.id === event.pointerId && event.timeStamp - tap.time < 450 && Math.hypot(event.clientX - tap.sx, event.clientY - tap.sy) < 12) stir(record, tap, .025, -.02, true);
    record.tap = null;
  });
  listen(host, 'pointercancel', () => { record.tap = null; });
  listen(image, 'load', () => { if (record.url && record.url !== sourceURL(image)) release(record); record.failed = false; queueSync(); });
  resize.observe(image); intersection.observe(image); status(record, reduced.matches ? 'reduced-motion' : 'waiting');
}
function unregister(record) {
  record.active = false; release(record); record.abort.abort();
  record.button?.remove(); resize.unobserve(record.image); intersection.unobserve(record.image);
  record.host.classList.remove('mf-drink-motion-host');
  if (record.positioned) record.host.classList.remove('mf-drink-motion-positioned');
  delete record.host.dataset.mfMotionState; delete record.image.dataset.mfMotionState;
  records.delete(record.image);
}
const resize = new ResizeObserver(entries => { for (const entry of entries) { const r = records.get(entry.target); if (r) mapPhoto(r); } queueSync(); });
const intersection = new IntersectionObserver(entries => {
  for (const entry of entries) { const r = records.get(entry.target); if (r) { r.visible = entry.isIntersecting && entry.intersectionRatio >= .15; r.ratio = entry.intersectionRatio; } }
  queueSync();
}, { threshold: [0, .15, .4, .7, 1] });
function scan() { document.querySelectorAll('main img').forEach(register); queueSync(); }
new MutationObserver(mutations => {
  let added = false;
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      const record = records.get(mutation.target);
      if (record) { release(record); record.failed = false; record.profile = profileFor(record.image); }
    } else if ([...mutation.addedNodes].some(node => node.nodeType === 1 && (node.tagName === 'IMG' || node.querySelector?.('img')))) added = true;
  }
  if (added) scan(); else queueSync();
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
reduced.addEventListener('change', queueSync); coarse.addEventListener('change', queueSync);
document.addEventListener('visibilitychange', queueSync);
window.addEventListener('resize', () => { for (const record of records.values()) mapPhoto(record); queueSync(); }, { passive: true });
window.addEventListener('pagehide', () => { suspended = true; cancelAnimationFrame(running); running = 0; for (const record of records.values()) { record.active = false; release(record); status(record, 'hidden'); } });
window.addEventListener('pageshow', () => { suspended = false; queueSync(); });
document.addEventListener('shopify:section:load', scan);
document.addEventListener('shopify:section:unload', event => { for (const record of records.values()) if (event.target.contains(record.image)) unregister(record); });
window.__mfDrinkMotion = { version: 1, profiles: PROFILES, inspect: () => ({ paused, reduced: reduced.matches, contextLimit: coarse.matches || innerWidth < 761 ? 1 : 2, records: [...records.values()].map(r => ({ id: r.id, profile: r.profile?.name || null, state: r.state, visible: r.visible, active: !!r.fluid, mapping: r.mapping, error: r.error || null, stats: r.fluid?.getStats() || null })) }) };
scan();
